import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Snackbar, Alert } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import theme from './theme';
import './App.css';
import './styles/pwa-touch.css';
import { AuthProvider, useAuth } from './components/AuthContext';
import { RoutineExecutionProvider } from './contexts/RoutineExecutionContext';
import { TaskDialogProvider } from './contexts/TaskDialogContext';
import { ModulesProvider } from './contexts/ModulesContext';
import PWAHandler from './components/PWAHandler';
import PWAManager from './components/PWAManager';

const MainLayout = lazy(() => import('./components/MainLayout'));
const AuthForm = lazy(() => import('./components/AuthForm'));
const ObjectView = lazy(() => import('./components/ObjectView'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,      // 30s: data is fresh, won't refetch
      gcTime: 12 * 60 * 60 * 1000, // 12h: keep in memory after unmount
      retry: 1,
      refetchOnWindowFocus: false, // avoid surprise refetches on mobile app-switch
    },
  },
});


function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <RoutineExecutionProvider>
            <TaskDialogProvider>
              <ModulesProvider>
                <Router>
                  <AuthGate />
                </Router>
              </ModulesProvider>
            </TaskDialogProvider>
          </RoutineExecutionProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function AuthGate() {
  const { user, loading, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentMsg, setPaymentMsg] = useState(null);
  
  // Handle payment redirect params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      setPaymentMsg({ severity: 'success', text: 'Zahlung erfolgreich! Dein Plan wird in Kürze aktualisiert.' });
      if (refreshUser) refreshUser();
      navigate('/', { replace: true });
    } else if (payment === 'canceled') {
      setPaymentMsg({ severity: 'info', text: 'Zahlung abgebrochen.' });
      navigate('/', { replace: true });
    }
  }, [location.search, navigate, refreshUser]);
  
  if (loading) return null;
  
  // Allow direct object URLs without authentication
  const isObjectRoute = location.pathname.startsWith('/object/');
  const isResetRoute = location.pathname.startsWith('/reset-password');
  
  if (!user && !isObjectRoute && !isResetRoute) {
    return <Suspense fallback={null}><AuthForm /></Suspense>;
  }
  
  return (
    <>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={user ? <MainLayout /> : <AuthForm />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/object/:id" element={<ObjectView />} />
        </Routes>
      </Suspense>
      <PWAHandler />
      <PWAManager />
      <Snackbar
        open={!!paymentMsg}
        autoHideDuration={6000}
        onClose={() => setPaymentMsg(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {paymentMsg && (
          <Alert onClose={() => setPaymentMsg(null)} severity={paymentMsg.severity} variant="filled">
            {paymentMsg.text}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}

export default App;
